import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, Input, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IEndpoint } from "@auvious/rtc";
import { merge } from "rxjs";
import { slideIn } from "../../core-ui.animations";
import { ConferenceMetadataKeyEnum, SizeEnum } from "../../core-ui.enums";
import { DEFAULT_CALL_HOLD_STATE, ICallHoldState } from "../../models";
import {
  ArPointerActivatedEvent,
  ConferenceService,
  ConferenceStore,
  DeviceCaptureErrorAction,
  LocalMediaService,
  PointerService,
  SnapshotService,
  StreamState,
  windowActionType,
  WindowEventService,
} from "../../services";
import { CobrowseService } from "../../services/cobrowse.service";
import { MediaRulesService } from "../../services/media.rules.service";
import { RoomComponent } from "../room/room.component";

@Component({
  selector: "app-display-capture",
  templateUrl: "./display-capture.component.html",
  styleUrls: ["./display-capture.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideIn],
  standalone: false,
})
export class DisplayCaptureComponent implements OnInit {
  private room = inject(RoomComponent, { optional: true });
  private mediaRules = inject(MediaRulesService);
  private conferenceService = inject(ConferenceService);
  private windowEventService = inject(WindowEventService);
  private cdr = inject(ChangeDetectorRef);
  private pointerService = inject(PointerService);
  private cobrowseService = inject(CobrowseService);
  private local = inject(LocalMediaService);
  private snapshotService = inject(SnapshotService);
  private store = inject(ConferenceStore);
  private destroyRef = inject(DestroyRef);

  @Input() size: SizeEnum;
  @Input() screenStream: StreamState;
  @Input() type: "button" | "menu-item" = "button";

  callHoldState: ICallHoldState;
  isPointerActive = false;
  isCobrowseOn = false;
  isDisabledFromWidget = false;
  snapshotStream: StreamState;
  isTypeButton = false;
  isTypeMenuItem = false;

  constructor() {
    this.callHoldState = { ...DEFAULT_CALL_HOLD_STATE };
  }

  @HostBinding("class") get cssClass() {
    return {
      "type-button": this.type == "button",
    };
  }

  ngOnInit(): void {
    this.isTypeButton = this.type === "button";
    this.isTypeMenuItem = this.type === "menu-item";

    // the menu item is created when the menu opens, possibly while the call is already on hold
    // or a pointer/co-browse session is running. The streams below only report later changes
    this.callHoldState =
      this.conferenceService.getCallHoldState() ?? this.callHoldState;
    this.isPointerActive = this.pointerService.isStarted;
    this.isCobrowseOn =
      this.cobrowseService.isStarted || this.cobrowseService.isPendingApproval;
    this.conferenceService.callHoldStateChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
        this.callHoldState = state;
        this.cdr.markForCheck();
      });
    this.pointerService.pointAreaChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((d) => {
        this.isPointerActive = d.type === ArPointerActivatedEvent.type;
        this.cdr.detectChanges();
      });
    merge(
      this.cobrowseService.participantInvited$,
      this.cobrowseService.started$
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
        this.isCobrowseOn = true;
        this.cdr.detectChanges();
      });
    this.cobrowseService.ended$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
        this.isCobrowseOn = false;
        this.cdr.detectChanges();
      });

    merge(
      // snapshot started for us as customers
      this.snapshotService.started$,
      this.snapshotService.ended$
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e) => {
        this.cdr.detectChanges();
      });

    // snapshot started for us as agents
    this.store.updated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.snapshotStream !== this.store.snapshotStream) {
          this.snapshotStream = this.store.snapshotStream;
          this.cdr.detectChanges();
        }
      });

    if (this.room.isChildWindow) {
      this.windowEventService
        .init(this.room.interaction.getParentFrameUrl())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((action) => {
            if (!action) {
              return;
            }
            switch (action.type) {
              case windowActionType.SCREEN_SHARE_AVAILABILITY_CHANGED:
                this.isDisabledFromWidget = !action.payload;
                this.cdr.detectChanges();
                break;
              case windowActionType.DEVICE_CAPTURE:
                if (!this.isScreenShareAvailable) {
                  const error = new Error("screen-share-unvailable");
                  error.name = "BadStateError";
                  return this.windowEventService.sendMessage(
                    new DeviceCaptureErrorAction(error)
                  );
                }
                if (this.isOnHold) {
                  const error = new Error("on-hold");
                  error.name = "BadStateError";
                  return this.windowEventService.sendMessage(
                    new DeviceCaptureErrorAction(error)
                  );
                }
                // if (this.isScreenShareLocal) {
                //     // already sharing, stop
                // }
                this.toggleScreenShare();
                break;
              case windowActionType.DEVICE_CAPTURE_STOP:
                this.toggleScreenShare();
                break;
            }
          });
    }
  }

  /* actions */

  async toggleScreenShare() {
    if (this.isScreenShareActive) {
      this.local.closeScreenStream();
    } else {
      this.mediaRules.dismissPendingConfirmations();
      this.local.shareScreen({ enforceSurface: true });
    }
  }

  /* view getters */

  get isDisabled() {
    return (
      this.isScreenShareActive ||
      this.isOnHold ||
      this.isPointerActive ||
      this.isCobrowseOn ||
      this.isSnapshotActive ||
      this.isDisabledFromWidget
    );
  }

  get isSnapshotActive() {
    return this.snapshotService.isStarted;
  }

  get isScreenShareFullScreen() {
    return this.room.isAudioCall && this.room.isWidget;
  }

  get isOnHold(): boolean {
    return this.callHoldState.isOnHold;
  }
  get isHoldLocal(): boolean {
    return this.callHoldState.username === this.myself.username;
  }

  get isScreenShareActive(): boolean {
    return !!this.screenStream;
  }

  get isScreenShareAvailable(): boolean {
    return this.mediaRules.isDisplayCaptureAvailable;
  }

  get isScreenShareLocal(): boolean {
    return this.screenStream?.originator.endpoint === this.myself.endpoint;
  }

  private get myself(): IEndpoint {
    return {
      username: this.store.username,
      endpoint: this.store.endpoint,
    };
  }
}
